import { KeyboardArrowDown } from '@mui/icons-material';
import { Autocomplete as MAutocomplete, Paper, styled } from '@mui/material';

import FHInputField from './FHInputField';
import { DEFAULT_INPUT_HEIGHT, Themes } from '../../constants';
import { FORTA_BLACK, FORTA_WHITE, FORTA_ERROR, FORTA_GRAY2, FORTA_BLUE } from '../../utils/colors';
import { getThemeColor } from '../../utils/common';

import type {
  PaperProps as MPaperProps,
  AutocompleteProps as MAutocompleteProps,
} from '@mui/material';

interface PaperProps extends MPaperProps {
  colorTheme?: Themes;
  coloredBackground?: boolean;
}

interface AutocompleteProps extends MAutocompleteProps<any, boolean, boolean, boolean> {
  inputHeight?: number;
  coloredBackground?: boolean;
  minWidth?: number;
  error: boolean;
}

const StyledAutocomplete = styled(MAutocomplete, {
  shouldForwardProp: (prop) =>
    prop !== 'inputHeight' &&
    prop !== 'coloredBackground' &&
    prop !== 'minWidth' &&
    prop !== 'colorTheme' &&
    prop !== 'error' &&
    prop !== 'allowClear',
})<AutocompleteProps>(({ inputHeight, coloredBackground, minWidth, error, sx = {} }) => ({
  '& .MuiFormControl-root': {
    borderRadius: '8px',
    height: inputHeight,
    border: error ? `inset 2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
    '&:focus-within': {
      border: error ? `inset 2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
    },
    minWidth,
    '& .MuiOutlinedInput-root': {
      padding: '0 0.5em',
    },
    '&.Mui-disabled': {
      color: FORTA_BLACK,
      backgroundColor: coloredBackground ? FORTA_GRAY2 : FORTA_WHITE,
    },
    '& .MuiIconButton-root.Mui-disabled': {
      opacity: 0.2,
    },
    '& .MuiOutlinedInput-input': {
      height: inputHeight,
    },
    '& .MuiInputLabel-root': {
      transform: 'translate(14px, 4px) scale(1)',

      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -12px) scale(.75)',
      },
    },
    '& input::placeholder': {
      color: FORTA_BLACK,
      opacity: 1,
    },
    ...sx,
  },
}));

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'colorTheme' && prop !== 'coloredBackground',
})<PaperProps>(({ colorTheme, coloredBackground }) => {
  const selectedTheme = colorTheme ?? Themes.Primary;

  return {
  borderRadius: '8px',
  backgroundColor: coloredBackground ? getThemeColor(selectedTheme).light3 : FORTA_WHITE,
  '& ::-webkit-scrollbar': {
    width: '12px',
  },
  '& ::-webkit-scrollbar-track': {
    background: getThemeColor(selectedTheme).light3,
    borderRadius: '6px',
  },
  '& ::-webkit-scrollbar-thumb': {
    background: getThemeColor(selectedTheme).primary,
    borderRadius: '10px',
    border: `5px solid ${getThemeColor(selectedTheme).light3}`,
  },
  '& .MuiAutocomplete-listbox': {
    '& .MuiAutocomplete-option': {
      backgroundColor: coloredBackground ? getThemeColor(selectedTheme).light3 : FORTA_WHITE,
    },
    '& .MuiAutocomplete-option.Mui-focused': {
      backgroundColor: `${getThemeColor(selectedTheme).primary} !important`,
      color: FORTA_WHITE,
    },
    '& .MuiAutocomplete-option:hover': {
      backgroundColor: getThemeColor(selectedTheme).primary,
      color: FORTA_WHITE,
    },
    '& .MuiAutocomplete-option.Mui-selected': {
      backgroundColor: getThemeColor(selectedTheme).primary,
      color: FORTA_WHITE,
    },
  },
  };
});

const FHAutocomplete = (props: any) => {
  const {
    placeholder,
    label,
    helperText = '',
    error = false,
    colorTheme = Themes.Primary,
    coloredBackground = false,
    inputHeight = DEFAULT_INPUT_HEIGHT,
  } = props;

  return (
    <StyledAutocomplete
      {...props}
      onKeyDown={(e: any) => {
        // Prevent parent containers (e.g., MUI Menu/List) from intercepting keystrokes
        // which can cause focus to shift away from the input when typing.
        e.stopPropagation();
        if (props?.onKeyDown) props.onKeyDown(e);
      }}
      renderInput={(params) => (
        <FHInputField
          {...params}
          error={error}
          helperText={helperText}
          label={label}
          placeholder={placeholder}
          inputHeight={inputHeight}
          coloredBackground={coloredBackground}
        />
      )}
      PaperComponent={({ children }) => (
        <StyledPaper colorTheme={colorTheme} coloredBackground={coloredBackground}>
          {children}
        </StyledPaper>
      )}
      popupIcon={<KeyboardArrowDown sx={{ color: getThemeColor(colorTheme).primary }} />}
    />
  );
};

export default FHAutocomplete;
